/**
 * 
 * @copyright     (c) 2025 Gist Applications Inc.
 * @author        Greg Olive greg@gist-apps.com
 * @package       gift-card-pro
 * 
 * /assets/js/React/_utils/PricingUtils.ts
 * Created:       Tue Dec 16 2025
 * Modified By:   Greg Olive
 * Last Modified: Tue Dec 16 2025
 */

import { formatIntlMoney } from "@root/_utils";
import { ICurrencyType, IMoneyType, IFulfillmentType, IAmountChoicesType } from "../_types/currency";



export const GCP_PRICING = {

  SPECIAL_CURRENCIES: ["CVE", "DJF", "GNF", "IDR", "JPY", "KMF", "KRW", "PYG", "RWF", "UGX", "VND", "VUV"],

};

export const isTrue = (value: any) => {
  return value === true || value === "1" || value === "true" || value === 1;
}

/**
 * Get physical surcharge price details
 * @param settings 
 * @param currency 
 * @param shouldConvert 
 * @param fulfillmentType 
 * @returns 
 */
export const getSurchargeDetails = (
  settings: any, 
  currency: ICurrencyType,
  fulfillmentType: IFulfillmentType
) => {

  let surchargeAmount = settings?.physical?.giftcard?.surcharge_amount || null;

  if (typeof(surchargeAmount) === 'string') {
    surchargeAmount = [];
  }
  
  if (!surchargeAmount || !settings || !settings.physical || !Object.keys(settings).length || fulfillmentType !== "physical") {
    return { value: null, formatted: null }
  };

  const currencyCode = currency?.active || settings?.shop?.currency;

  const activeSurcharge = surchargeAmount.find((surcharge: any) => surcharge.currency === currencyCode);
  
  if (!activeSurcharge) {
    return { value: null, formatted: null }
  }

  const amount = Math.round(parseFloat(activeSurcharge.value) * 1e2) / 1e2;

  return {
    value: amount,
    formatted: formatGiftCardValue(amount, currency),
  }


}

/**
 * Decide when to add a custom value to the gift card values.
 * @param {*} values 
 * @returns {boolean}
 */
export const customEnabled = (
  values: any[], 
  settings: any,
  fulfillmentType: IFulfillmentType
) => {

  if (fulfillmentType === "physical" && isTrue(settings?.physical?.giftcard?.allow_custom)) {
    return true;
  }

  if (fulfillmentType === "digital" && isTrue(settings?.digital?.giftcard?.allow_custom)) {
    return true;
  }

  if (values.length === 0) {
    return true;
  }

  return false;

}

/**
 * Fetch the pricing configuration based on the currency and physical/digital sending.
 * @param {*} fulfillmentType 
 * @param {*} currency 
 * @returns 
 */
export const getPricingConfig = (
  fulfillmentType: IFulfillmentType,
  currency: ICurrencyType
) => {

  const { SPECIAL_CURRENCIES } = GCP_PRICING;

  const { active, shop } = currency;

  const isSpecialCurrency = (SPECIAL_CURRENCIES.includes(active) && SPECIAL_CURRENCIES.includes(shop));
  

  return {
    isSpecialCurrency,
  }

}

/**
 * Update the price of the gift card based on the selected value for display purposes.
 * @returns 
 */
export const getSelectedGiftCardCost = (
  amountChoices: IAmountChoicesType,
  pricingConfig: any, 
  formData: any, 
  inDashboard: boolean,
  preview: boolean,
  currency: ICurrencyType,
  ): IMoneyType => {

  if (!amountChoices || !amountChoices?.values?.length || !formData.id || !pricingConfig) {
    return { value: null, formatted: null };
  }

  let cost = 0;

  // The variant id is stored in value.value of the amountChoices object
  const selectedVariant      = amountChoices?.values?.find((value: any) => value.value.toString() === formData.id.toString());
  const centsConversion      = (pricingConfig.isSpecialCurrency) ? 1e2 : 1;
 
  if (formData.id == "Custom") {
    cost = formData.properties._gcp_custom_value * centsConversion;
    return { 
      value: cost,
      formatted: formatGiftCardValue(cost, currency) 
    }
  }

  // if (preview || inDashboard) {
  //   cost = parseInt(formData.id);
  //   return { 
  //     value: cost,
  //     formatted: formatGiftCardValue(cost, currency) 
  //   }
  // }

  if (!selectedVariant) {
    return { value: 0, formatted: formatGiftCardValue(0, currency) };
  }

  // The formatted value is stored in the label of the amountChoices object
  return { 
    value: selectedVariant.price,
    formatted: selectedVariant.label
  };


};

/**
 * Format gift card value using moneyFormat or internationally.
 * @param {*} value
 * @returns
 */
export const formatGiftCardValue = (
  value: number, 
  currency: ICurrencyType
) => {

  const locale = window?.Shopify?.locale || "en";
  const moneyFormat = currency.moneyFormat || window?.Shopify?.money_format || "{{amount}}";
  return formatIntlMoney(value, currency.active, locale, moneyFormat);

};

/**
 * Get the gift card values to display in the form.
 * @param {*} filteredVariants 
 * @returns 
 */
export const getGiftCardValues = (
  settings: any,
  fulfillmentType: IFulfillmentType,
  inDashboard: boolean,
  currency: ICurrencyType,
  physicalSurcharge: IMoneyType,
  bulkOrder: boolean
) => {

  const subSettings = fulfillmentType ? settings[fulfillmentType] : {};
  // const presetValues = subSettings?.giftcard?.preset_prices
  
  // const values = inDashboard ? 
  
  // getDashboardValues(
  //   presetValues, 
  //   currency
  // ) : 
  const values = getStorefrontValues(
    subSettings, 
    currency, 
    physicalSurcharge,
    fulfillmentType
  );

  if (customEnabled(values, settings, fulfillmentType) === true && bulkOrder === false) {
    values.push({
      value: "Custom",
      label: settings.translations?.purchase?.custom_label || "Custom"
    });
  }

  return values;
}

/**
 * In storefront -> display c corresponding to the gift card product
 * @param {*} variants
 * @param {*} presetValues
 * @returns
 */
export const getStorefrontValues = (
  subSettings: any,
  currency: ICurrencyType, 
  physicalSurcharge: IMoneyType,
  fulfillmentType: IFulfillmentType,
) => {
    
  return subSettings.giftcard.active_currency_prices.map((variant: any) => {

    // Always convert to cents - even for special currencies    
    let priceCents = Math.round(Number(variant.contextualPricing.price.amount) * 1e2);

    if (
      fulfillmentType === "physical" && 
      physicalSurcharge.value
    ) {
      let surchargeValue = physicalSurcharge.value * 1e2;
      priceCents = priceCents - surchargeValue;
    }

    let price = priceCents / 1e2;

    // Converts the gid to REST API variant ID
    const variantId = parseInt(variant.id.replace("gid://shopify/ProductVariant/", ""));
    
    return {
      value: variantId,
      label: formatGiftCardValue(price, currency),
      price: price,
      priceCents: priceCents,
      currency: currency
    };

  });
    
    

};




/**
 * Filter out digital/physical variants when both fulfillment options are enabled.
 * 
 * Not in use in favour of active_markets_pricing settings object
 * 
 * @param {*} variants
 * @param {*} fulfillmentType
 * @returns
 */
export const filterVariants = (
  variants: any[], 
  fulfillmentType: IFulfillmentType
) => {

  const filtered = variants.filter((variant) => {
    
    if (typeof(variant.options) != "undefined") {

      if (fulfillmentType === "physical" && variant?.options[0] === "Physical") {
        return true;
      }

      if (fulfillmentType === "digital" && variant.options[0] === "Digital") {
        return true;
      }

    } else {

      if (fulfillmentType === "physical" && variant?.option1 === "Physical") {
        return true;
      }
  
      if (fulfillmentType === "digital" && variant?.option1 === "Digital") {
        return true;
      }
      

    }

    return false;

  });

  return filtered;
};

/**
 * In dashboard -> display presetValues from dirty settings for live preview
 * @param {*} presetValues
 * @returns
 */
export const getDashboardValues = (presetValues: any, currency: ICurrencyType) => {
  if (!presetValues || !Array.isArray(presetValues)) return [];

  return presetValues.map((val) => {
    return {
      value: val,
      label: formatGiftCardValue(val, currency)
    };
  });
};

/**
 * Get the currency conversion fee for international orders.
 * @param {*} settings 
 * @param {*} pricingConfig 
 * @param {*} formData 
 * @param {*} currency 
 * @param {*} giftCardCost 
 * @param {*} fulfillmentType 
 * @returns 
 */
export const getConversionFee = (
  pricingConfig: any,
  formData: any,
  currency: ICurrencyType,
  giftCardCost: IMoneyType,
  fulfillmentType: IFulfillmentType,
  physicalSurcharge: IMoneyType
): IMoneyType => {

  const shouldCalculate = formData.id === "Custom" && currency.isInternational;

  if (!giftCardCost.value || !shouldCalculate) {
    return {
      value: null,
      formatted: null
    };
  }
  const surchargeValue = ((fulfillmentType === "physical" && physicalSurcharge.value) ? physicalSurcharge.value : 0)
  const subtotal = giftCardCost.value + surchargeValue;
  const conversionFee = Math.round(subtotal * 2) / 1e2;

  return { value: conversionFee, formatted: formatGiftCardValue(conversionFee, currency) };

}

/**
 * Get the active currency on the online store
 * @param {*} settings 
 * @returns 
 */
export const getActiveCurrency = (settings: any) => {

  if (typeof(window.Shopify) !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) {
    return window.Shopify.currency.active;
  }

  if (typeof(settings) === 'undefined' || !settings.shop || !settings.shop.currency) {
    return "";
  }
  
  return settings.shop.currency;

}

/**
 * Check if the current active currency is international
 * @param {*} activeCurrency 
 * @param {*} settings 
 * @returns {boolean}
 */
export const isInternational = (activeCurrency: string, settings: any, inDashboard: boolean) => {

  if (inDashboard || !settings || !settings.shop || !settings.shop.enabled_presentment_currencies) {
    return false;
  }

  if (
    settings?.shop.enabled_presentment_currencies.length &&
    settings?.shop.enabled_presentment_currencies.includes(activeCurrency) &&
    settings?.shop.currency !== window?.Shopify?.currency?.active
  ) {
    return true;
  }

  return false;

}

/**
 * Load and store the currency details for the gift card form.
 * @param settings 
 * @param inDashboard 
 * @returns 
 */
export const getCurrencyDetails = (settings: any, inDashboard: boolean) => {
  
  const activeCurrency     = getActiveCurrency(settings);
  const activeCurrencyRate = typeof (window.Shopify) !== 'undefined' && window.Shopify.currency && window.Shopify.currency.rate ?  window.Shopify.currency.rate : 1;
  const isIntl             = isInternational(activeCurrency, settings, inDashboard);
  const activeCountry      = !inDashboard && typeof(window.Shopify) !== 'undefined' && window.Shopify.country ? window.Shopify.country : "US";

  const currency = {
    code: activeCurrency,
    symbol: (inDashboard) ? settings.shop.currency_symbol : window.Shopify.currency.active,
    active: (inDashboard) ? settings.shop.currency : activeCurrency,
    shop: settings?.shop?.currency,
    rate: (inDashboard) ? 1 : activeCurrencyRate,
    country: activeCountry,
    isInternational: isIntl,
    moneyFormat: settings?.shop?.money_format
  };


  return currency;
  
}

